import { AnswerItem, CustomBottomBar, CustomPageTitle, MinimalStepper } from '@components';
import { Button, causeriesWelcome, Container, CustomIcon, Typography, Icon } from '@design-system';
import React from 'react';
import { BiSolidChat } from 'react-icons/bi';
import { useCauseriesTalkQuestionsPageVM } from './useCauseriesTalkQuestionsPageVM';

import { MdKeyboardArrowRight } from 'react-icons/md';
import { PiThumbsDownDuotone, PiThumbsUpDuotone } from 'react-icons/pi';
import { LiaArrowLeftSolid } from 'react-icons/lia';
import './CauseriesTalkQuestionsPage.scss';
import { GrFormCheckmark } from 'react-icons/gr';

export const CauseriesTalkQuestionsPage: React.FC = () => {
    const {
        pageTitle,
        questions,
        isLoading,
        errorMessage,
        selectAnswer,
        explanationsToDisplay,
        isEveryQuestionAnswered,
        showResults,
        currentScore,
        navigate,
        validated,
        setValidated,
        currentQuestionIndex,
        canGoBack,
        canGoNext,
        isLastQuestion,
        handleNext,
        handlePrev,
        thematicId,
        talkId
    } = useCauseriesTalkQuestionsPageVM();

    if (!questions && errorMessage) {
        return (
            <Container>
                <Typography intrinsic="p" color="red">
                    {errorMessage}
                </Typography>
            </Container>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];

    return (
        <div className="causeries-talk-questions-page">
            {pageTitle && (
                <CustomPageTitle>
                    <Typography intrinsic="h3" iconColor="blue" icon={<BiSolidChat />} iconFilled iconSize={20}>
                        {pageTitle}
                    </Typography>
                </CustomPageTitle>
            )}

            <Container className="questions-page-header" radius="none" column>
                <Typography intrinsic="h2" className="mb-large-spacing">
                    Le quizz
                </Typography>
                <MinimalStepper
                    steps={[
                        { label: 'Les instructions', status: 'completed' },
                        { label: 'Le quizz', status: isEveryQuestionAnswered ? 'completed' : 'inProgress' },
                        { label: 'Emargement', status: 'pending' }
                    ]}
                />
            </Container>

            {showResults ? (
                <>
                    {validated ? (
                        <Container>
                            <Container className="results-container" bordered="grey" padding="medium large">
                                <CustomIcon src={causeriesWelcome} alt="Causeries" className="mt-large-spacing mb-small-medium-spacing" />

                                <Typography intrinsic="h1" color="grey">
                                    Causerie validée
                                </Typography>
                                <Typography intrinsic="h2" color="grey" className="mb-x-large-spacing">
                                    {pageTitle}
                                </Typography>
                                <div className="check mb-medium-spacing">
                                    <GrFormCheckmark color="#00974a" size={46} />
                                </div>
                            </Container>
                        </Container>
                    ) : (
                        <Container>
                            <Container className="results-container" bordered="grey" padding="medium large">
                                <Typography intrinsic="h4" className="mb-medium-spacing text-left">
                                    Résultats
                                </Typography>
                                <div className="results-container-content">
                                    <Typography intrinsic="h4" className="text-center mb-small-spacing">
                                        {currentScore > questions.length / 2 ? 'Félicitations !' : 'Dommage...'}
                                        <br /> Vous avez obtenu un score de :
                                    </Typography>
                                </div>
                                <div className={`results-container-score ${currentScore > questions.length / 2 ? 'success' : 'error'}`}>
                                    <Typography intrinsic="h1" color={currentScore > questions.length / 2 ? 'green' : 'red'}>
                                        {currentScore}/{questions.length}
                                    </Typography>
                                </div>
                                <Typography
                                    intrinsic="p"
                                    color="marine"
                                    className="mt-large-spacing mb-small-spacing px-large-spacing text-center"
                                >
                                    {currentScore > questions.length / 2
                                        ? "Veuillez passer à l'étape suivante pour valider le module."
                                        : 'Courage, vous ferez mieux la prochaine fois !'}
                                </Typography>
                            </Container>
                        </Container>
                    )}
                </>
            ) : (
                <>
                    {isLoading ? (
                        <Typography intrinsic="p">Chargement...</Typography>
                    ) : (
                        currentQuestion && (
                            <Container className="questions-container">
                                <Container padding="none" className="question-container-wrapper">
                                    <Container bordered="grey" className="question-container">
                                        <Typography intrinsic="p" className="question-title" defaultSpacing>
                                            Question {currentQuestionIndex + 1} / {questions.length}
                                        </Typography>
                                        <Typography intrinsic="h4" defaultSpacing>
                                            {currentQuestion.text}
                                        </Typography>
                                        <div className="answers-container">
                                            {currentQuestion.talk_answers.map((answer) => (
                                                <AnswerItem
                                                    key={answer.id}
                                                    answer={answer.text}
                                                    isRight={answer.is_correct}
                                                    isSelected={currentQuestion.answer === answer.id}
                                                    display={currentQuestion.answerToDisplay === answer.id}
                                                    onClick={() => {
                                                        selectAnswer(currentQuestion.id, answer.id);
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </Container>

                                    {explanationsToDisplay.includes(currentQuestion.id) && (
                                        <Container bordered="grey" className="explanation-container">
                                            {currentQuestion.talk_answers.find((answer) => answer.is_correct)?.id ===
                                            currentQuestion.answer ? (
                                                <Typography
                                                    icon={<PiThumbsUpDuotone />}
                                                    iconColor="green"
                                                    intrinsic="p"
                                                    className="explanation-title"
                                                    defaultSpacing
                                                    color="green"
                                                >
                                                    Bonne réponse !
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    icon={<PiThumbsDownDuotone />}
                                                    iconColor="red"
                                                    iconSize={20}
                                                    intrinsic="p"
                                                    className="explanation-title"
                                                    defaultSpacing
                                                    color="red"
                                                >
                                                    Mauvaise réponse...
                                                </Typography>
                                            )}

                                            <Typography intrinsic="p" className="explanation-text px-small-spacing">
                                                {currentQuestion.explanation}
                                            </Typography>
                                        </Container>
                                    )}
                                </Container>
                            </Container>
                        )
                    )}
                </>
            )}

            <CustomBottomBar>
                {!showResults ? (
                    <>
                        <Button
                            color="marine"
                            variant="secondary"
                            onClick={handlePrev}
                            disabled={!canGoBack}
                            className="sm-bottom-bar-button mr-small-spacing"
                        >
                            <Icon className="arrow-left" color="grey" icon={<LiaArrowLeftSolid />} size={30} />
                        </Button>
                        <Button
                            variant="primary"
                            color="marine"
                            size="fullwidth"
                            onClick={handleNext}
                            disabled={!canGoNext}
                            className="next-button"
                            icon={!isLastQuestion ? <MdKeyboardArrowRight /> : undefined}
                            iconPosition="forced-right"
                            iconColor="white"
                            iconSize={30}
                        >
                            <Typography intrinsic="p">{isLastQuestion ? 'Voir les résultats' : 'Suivant'}</Typography>
                        </Button>
                    </>
                ) : (
                    <Button
                        color="marine"
                        variant="primary"
                        size="fullwidth"
                        icon={validated ? undefined : <MdKeyboardArrowRight />}
                        iconPosition="forced-right"
                        iconColor="white"
                        iconSize={30}
                        className="bottom-bar-button"
                        disabled={!isEveryQuestionAnswered}
                        onClick={() => {
                            if (!validated) {
                                setValidated(true);
                            } else {
                                navigate(`/sante-securite/causeries/list/${thematicId}/emargement/${talkId}`);
                            }
                        }}
                    >
                        <Typography intrinsic="p">{validated ? 'Émargement' : "Passez à l'étape suivante"}</Typography>
                    </Button>
                )}
            </CustomBottomBar>
        </div>
    );
};
